import { Box, TableCell, TableHead, TableRow, useTheme } from "@mui/material";
import React, { FC } from "react";
import { SORT_DIRECTION, TABLE_HEADER_ARRAY } from "../../utils/Types";
import Icons from "../../assets/Icons";
import { globalStyles } from "../../styles/GlobalStyles";
import { SORT_DETAILS } from "./DynamicTable";
import { useTranslation } from "react-i18next";

export const CHECKBOX_COL_WIDTH = 46;
export const CASE_ID_COL_WIDTH = 140;

export type DynamicTableHeadProps = {
  data?: TABLE_HEADER_ARRAY;
  sortDetails?: SORT_DETAILS;
  setSortDetails?: (details: SORT_DETAILS) => void;
  enableRowSelection?: boolean;
  renderHeaderMenu?: () => React.ReactNode;
};


const DynamicTableHead: FC<DynamicTableHeadProps> = ({
  data = [],
  sortDetails,
  setSortDetails,
  enableRowSelection = false,
  renderHeaderMenu,
}) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const sortKey = sortDetails?.sortKey;
  const sortDirection = sortDetails?.sortDirection;

  const getSortIcon = (key: string) => {
    if (!sortDetails) return null;
    if (sortKey === key && sortDirection === SORT_DIRECTION.ASCENDING)
      return <Icons.SortAscending />;
    if (sortKey === key && sortDirection === SORT_DIRECTION.DESCENDING)
      return <Icons.SortDescending />;
    return <Icons.Sort />;
  };

  const onHeaderItemPress = (key: string) => {
    if (!setSortDetails || !sortDetails) return;

    setSortDetails({
      sortKey: key,
      sortDirection:
        sortKey === key && sortDirection === SORT_DIRECTION.ASCENDING
          ? SORT_DIRECTION.DESCENDING
          : SORT_DIRECTION.ASCENDING,
    });
  };

  return (
    <TableHead>
      <TableRow
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          backgroundColor: palette.custom.background.primary,
        }}
      >
        {enableRowSelection && (
          <TableCell
            padding="checkbox"
            sx={{
              position: "sticky",
              left: 0,
              top: "unset",
              width: CHECKBOX_COL_WIDTH,
              zIndex: 6,
              backgroundColor: palette.custom.background.primary,
            }}
          >
            <Box display="flex" justifyContent="center">
              {renderHeaderMenu?.()}
            </Box>
          </TableCell>
        )}

        {data.map((item) => {
          const { key, label, position = "left", width } = item;
          const isCaseId = enableRowSelection && key === "caseId";
          const leftOffset = isCaseId ? CHECKBOX_COL_WIDTH : null;
          const cellPosition = isCaseId ? "sticky" : "static";
          const cellZIndex = isCaseId ? 3 : 1;
          const minCellWidth = width ?? CASE_ID_COL_WIDTH;
          const canSort = Boolean(sortDetails && setSortDetails);
          const isSortableColumn =
            canSort && label && label !== "EMR" && key !== "expand" && key !== "interval";
          const handleHeaderClick = isSortableColumn
            ? () => onHeaderItemPress(key)
            : undefined;
          const justifyContentMap: Record<string, string> = {
            right: "flex-end",
            center: "center",
            left: "flex-start",
          };
          const justifyContent = justifyContentMap[position] ?? "flex-start";
          return (
            <TableCell
              key={key}
              align={position}
              onClick={handleHeaderClick}
              sx={{
                fontSize: 16,
                fontWeight: 400,
                minWidth: minCellWidth,
                position: cellPosition,
                left: leftOffset,
                zIndex: cellZIndex,
                backgroundColor: palette.custom.background.primary,
              }}
            >
              <Box
                sx={{
                  ...globalStyles.center,
                  justifyContent,
                  whiteSpace: "nowrap",
                }}
                gap={1}
              >
                {t(label)}
                {isSortableColumn && getSortIcon(key)}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default DynamicTableHead;
